import { useEffect } from "react";
import { nextPageState } from "@/lib/paginationPageState";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZES = [10, 25, 50, 100];

const stateByTable = new WeakMap();
const controlByTable = new WeakMap();

// ── Page persistence across LIST → DETAIL → Back ─────────────────────────────
// The enhancer keys its live state on the <table> DOM node, which React
// recreates when a list unmounts — so the current page would reset to 1 on Back.
// We mirror {page, pageSize} into sessionStorage under a stable key (an opt-in
// `data-pagination-key`, else the pathname + the table's index) so the page is
// restored when the list re-mounts, and it survives a refresh (per tab). This is
// generic — every enhancer-managed table benefits, no per-page code.
const PERSIST_PREFIX = "tablepage:";

const keyForTable = (table) => {
  if (table.dataset.paginationKey) return table.dataset.paginationKey;
  const tables = document.querySelectorAll("table");
  const idx = Array.prototype.indexOf.call(tables, table);
  return `${window.location.pathname}#${idx < 0 ? 0 : idx}`;
};

const readPersisted = (key) => {
  try {
    const raw = sessionStorage.getItem(PERSIST_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const persist = (key, value) => {
  try {
    sessionStorage.setItem(PERSIST_PREFIX + key, JSON.stringify(value));
  } catch {
    /* private window / storage disabled — page restore is best-effort */
  }
};

const getRows = (table) => {
  const body = table.tBodies?.[0];
  if (!body) return [];
  return Array.from(body.rows).filter((row) => !row.dataset.paginationIgnore);
};

const getControlAnchor = (table) => {
  const wrapper = table.parentElement;
  if (
    wrapper &&
    wrapper.children.length === 1 &&
    wrapper.classList.contains("overflow-auto")
  ) {
    return wrapper;
  }
  return table;
};

const getPageWindow = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) return [1, 2, 3, 4, 5];
  if (currentPage >= totalPages - 2) {
    return Array.from({ length: 5 }, (_, index) => totalPages - 4 + index);
  }
  return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
};

const buttonClass = (active = false) =>
  [
    "h-8 min-w-8 rounded-md border px-2 text-xs font-medium transition-colors",
    active
      ? "border-indigo-600 bg-indigo-600 text-white"
      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
  ].join(" ");

const setButtonDisabled = (button, disabled) => {
  button.disabled = disabled;
  button.classList.toggle("cursor-not-allowed", disabled);
  button.classList.toggle("opacity-50", disabled);
};

const makeButton = ({ label, title, active, onClick }) => {
  const button = document.createElement("button");
  button.type = "button";
  button.textContent = label;
  button.title = title || label;
  button.className = buttonClass(active);
  button.addEventListener("click", onClick);
  return button;
};

const getState = (table) => {
  const existing = stateByTable.get(table);
  if (existing) return existing;

  const key = keyForTable(table);
  const persisted = readPersisted(key);
  const state = {
    key,
    // `everPaged` distinguishes "still loading (few rows yet)" from "genuinely a
    // small result set" so the loading frame can't clobber a restored page.
    everPaged: false,
    page: persisted?.page || 1,
    pageSize:
      persisted?.pageSize || Number(table.dataset.pageSize) || DEFAULT_PAGE_SIZE,
  };
  stateByTable.set(table, state);
  return state;
};

const showAllRows = (rows) => {
  rows.forEach((row) => {
    row.hidden = false;
  });
};

const ensureControl = (table) => {
  const existing = controlByTable.get(table);
  if (existing?.isConnected) return existing;

  const anchor = getControlAnchor(table);
  const control = document.createElement("div");
  control.dataset.tablePaginationControl = "true";
  control.className =
    "mt-3 flex flex-col gap-3 border-t border-slate-100 px-1 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between";
  anchor.insertAdjacentElement("afterend", control);
  controlByTable.set(table, control);
  return control;
};

const removeControl = (table) => {
  const control = controlByTable.get(table);
  if (control?.isConnected) control.remove();
  controlByTable.delete(table);
};

const renderControl = (table, rows, totalPages) => {
  const state = getState(table);
  const control = ensureControl(table);
  control.replaceChildren();

  const start = rows.length === 0 ? 0 : (state.page - 1) * state.pageSize + 1;
  const end = Math.min(state.page * state.pageSize, rows.length);

  const summary = document.createElement("div");
  summary.className = "text-xs text-slate-500";
  summary.textContent = `Showing ${start}-${end} of ${rows.length}`;

  const pageSizeWrap = document.createElement("label");
  pageSizeWrap.className = "flex items-center gap-2 text-xs text-slate-500";
  pageSizeWrap.textContent = "Rows";

  const pageSizeSelect = document.createElement("select");
  pageSizeSelect.className =
    "h-8 rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";
  PAGE_SIZES.forEach((size) => {
    const option = document.createElement("option");
    option.value = String(size);
    option.textContent = String(size);
    option.selected = size === state.pageSize;
    pageSizeSelect.appendChild(option);
  });
  pageSizeSelect.addEventListener("change", () => {
    state.pageSize = Number(pageSizeSelect.value) || DEFAULT_PAGE_SIZE;
    state.page = 1;
    paginateTable(table);
  });
  pageSizeWrap.appendChild(pageSizeSelect);

  const left = document.createElement("div");
  left.className = "flex flex-wrap items-center gap-3";
  left.append(summary, pageSizeWrap);

  const pages = document.createElement("div");
  pages.className = "flex flex-wrap items-center gap-1";

  const previous = makeButton({
    label: "Prev",
    title: "Previous page",
    onClick: () => {
      state.page = Math.max(1, state.page - 1);
      paginateTable(table);
    },
  });
  setButtonDisabled(previous, state.page === 1);
  pages.appendChild(previous);

  getPageWindow(state.page, totalPages).forEach((pageNumber) => {
    pages.appendChild(
      makeButton({
        label: String(pageNumber),
        title: `Page ${pageNumber}`,
        active: pageNumber === state.page,
        onClick: () => {
          state.page = pageNumber;
          paginateTable(table);
        },
      })
    );
  });

  const next = makeButton({
    label: "Next",
    title: "Next page",
    onClick: () => {
      state.page = Math.min(totalPages, state.page + 1);
      paginateTable(table);
    },
  });
  setButtonDisabled(next, state.page === totalPages);
  pages.appendChild(next);

  control.append(left, pages);
};

const paginateTable = (table) => {
  if (
    table.dataset.pagination === "off" ||
    table.dataset.noPagination === "true" ||
    table.closest("[data-table-pagination='off']")
  ) {
    removeControl(table);
    showAllRows(getRows(table));
    return;
  }

  const rows = getRows(table);
  const state = getState(table);

  const decision = nextPageState(
    { rowCount: rows.length, everPaged: state.everPaged, page: state.page, pageSize: state.pageSize },
    DEFAULT_PAGE_SIZE,
  );
  state.page = decision.page;
  state.everPaged = decision.everPaged;

  if (decision.mode === "showAll") {
    removeControl(table);
    showAllRows(rows);
    if (decision.persist) persist(state.key, { page: state.page, pageSize: state.pageSize });
    return;
  }

  const { totalPages } = decision;
  const start = (state.page - 1) * state.pageSize;
  const end = start + state.pageSize;
  rows.forEach((row, index) => {
    row.hidden = index < start || index >= end;
  });

  renderControl(table, rows, totalPages);
  persist(state.key, { page: state.page, pageSize: state.pageSize });
};

const enhanceTables = () => {
  document.querySelectorAll("table").forEach((table) => {
    if (table.closest("[data-table-pagination-control='true']")) return;
    paginateTable(table);
  });
};

export default function TablePaginationEnhancer() {
  useEffect(() => {
    let frameId = 0;
    const scheduleEnhance = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(enhanceTables);
    };

    scheduleEnhance();

    const isControlNode = (node) =>
      node instanceof HTMLElement &&
      (node.dataset?.tablePaginationControl === "true" ||
        !!node.querySelector?.("[data-table-pagination-control='true']"));

    const observer = new MutationObserver((mutations) => {
      const shouldRun = mutations.some((mutation) => {
        const target = mutation.target;
        if (!(target instanceof HTMLElement)) return false;
        // Ignore mutations INSIDE a control we injected.
        if (target.closest("[data-table-pagination-control='true']")) return false;
        // Ignore mutations that are ONLY our own control being added or removed.
        // Without this, React re-rendering the (React-managed) container removes
        // our injected control, the observer re-adds it, React removes it again —
        // an infinite add/remove loop that renders the pagination bar repeatedly.
        const touched = [...mutation.addedNodes, ...mutation.removedNodes];
        if (touched.length > 0 && touched.every(isControlNode)) return false;
        return true;
      });
      if (shouldRun) scheduleEnhance();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener("resize", scheduleEnhance);

    return () => {
      window.cancelAnimationFrame(frameId);
      observer.disconnect();
      window.removeEventListener("resize", scheduleEnhance);
      document
        .querySelectorAll("[data-table-pagination-control='true']")
        .forEach((control) => control.remove());
      document.querySelectorAll("table tbody tr[hidden]").forEach((row) => {
        row.hidden = false;
      });
    };
  }, []);

  return null;
}
